import {
  Alert,
  AutoComplete,
  Checkbox,
  Col,
  Form,
  Row,
  Typography,
} from "antd-v5";
import type { DefaultOptionType, RefSelectProps } from "antd-v5/es/select";
import classNames from "classnames";
import { compact, flatten, isEmpty, isString, map, trim } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { KeyValue } from "types/tenantConfig";

import ACButton from "ac-components/ac-button/ACButton";
import PlusIcon from "ac-components/ac-icons/PlusIcon";
import TrashIcon from "ac-components/ac-icons/TrashIcon";

import "./ACColumnFilterKeyValue.less";
import { ACColumnFilterKeyValueProps, TagOption, TagRowState } from "./types";
import {
  formatTagLabel,
  generateOutputTag,
  parseIncomingTag,
  stripQuotes,
} from "./utils";

const PARTIAL_MATCH_HELPER =
  'Add quotes around a "key" or "value" for an exact match — the other stays partial.';

const DEFAULT_ROW: TagRowState = { key: "", value: "", partialMatch: false };

/**
 * Filter option for AutoComplete
 */
export const filterOption = (
  inputValue: string,
  option: DefaultOptionType | undefined,
): boolean =>
  isString(option?.value) &&
  option?.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1;

const preventEnter = (e: React.KeyboardEvent) => {
  if (e.key === "Enter") {
    e.preventDefault();
  }
};

function ACColumnFilterKeyValue({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  visible,
  hideInfo,
  limit,
}: ACColumnFilterKeyValueProps) {
  const [form] = Form.useForm();
  const inputRef = useRef<RefSelectProps>(null);

  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
  const [partialMatchStates, setPartialMatchStates] = useState<boolean[]>([]);
  const [changed, setChanged] = useState(false);
  const [error, setError] = useState(false);

  // Check if any row has partial match enabled (to show helper message)
  const hasAnyPartialMatch = useMemo(
    () => partialMatchStates.some(Boolean),
    [partialMatchStates],
  );

  // Get tag keys for autocomplete
  const tagKeyOptions = useMemo(
    () => tagOptions.map((opt) => opt.key),
    [tagOptions],
  );

  // Get tag values for a specific key
  const getTagValues = useCallback(
    (keyIndex: number) => {
      const tags = form.getFieldValue("tags") || [];
      const selectedKey = stripQuotes(tags[keyIndex]?.key || "");
      return flatten(
        tagOptions
          .filter((opt) => opt.key === selectedKey)
          .map((opt) => opt.value),
      );
    },
    [tagOptions, form],
  );

  // Fetch tag options from API
  useEffect(() => {
    const { promise, cancel } = GlobalSettingAPI.getTagsLibrary();
    promise.then((res: { library: string[] }) => {
      const tagArray = map(res.library, (item) => {
        const [key, value] = item.split(":");
        const valueArray = value?.split(",").map((v) => v.trim()) || [];
        return { key, value: valueArray };
      });
      setTagOptions(tagArray);
    });
    return cancel;
  }, []);

  // Initialize form when visible changes
  useEffect(() => {
    if (visible) {
      const rows = isEmpty(selectedKeys)
        ? [DEFAULT_ROW]
        : selectedKeys.map(parseIncomingTag);

      form.setFieldsValue({ tags: rows });
      setPartialMatchStates(rows.map((r: TagRowState) => r.partialMatch));
      setChanged(false);
      setError(false);

      // Focus input after delay
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [visible, selectedKeys, form]);

  // Handle partial match checkbox change
  const handlePartialMatchChange = useCallback(
    (checked: boolean, index: number) => {
      setPartialMatchStates((prev) => {
        const newStates = [...prev];
        newStates[index] = checked;
        return newStates;
      });
      setChanged(true);
    },
    [],
  );

  // Handle form value changes
  const handleFormChange = useCallback(() => {
    setChanged(true);
    setError(false);
  }, []);

  // Handle add row
  const handleAdd = useCallback((add: (defaultValue?: TagRowState) => void) => {
    add(DEFAULT_ROW);
    setPartialMatchStates((prev) => [...prev, false]);
    setChanged(true);
  }, []);

  // Handle remove row
  const handleRemove = useCallback(
    (remove: (index: number) => void, index: number) => {
      remove(index);
      setPartialMatchStates((prev) => prev.filter((_, i) => i !== index));
      setChanged(true);
    },
    [],
  );

  // Apply filter
  const handleApply = useCallback(() => {
    const tags: TagRowState[] = form.getFieldValue("tags") || [];

    // Validate
    const hasEmptyKey = tags.some((row) => isEmpty(trim(row?.key)));
    if (hasEmptyKey) {
      setError(true);
      return;
    }

    // Generate output with partialMatch states
    const tagsWithPartialMatch = tags.map((tag, i) => ({
      ...tag,
      partialMatch: partialMatchStates[i] ?? false,
    }));

    const output: KeyValue[] = compact(
      tagsWithPartialMatch
        .filter((row) => !isEmpty(trim(row.key)))
        .map(generateOutputTag),
    );

    setSelectedKeys(output);
    confirm();
  }, [form, partialMatchStates, setSelectedKeys, confirm]);

  // Clear filter
  const handleClear = useCallback(() => {
    clearFilters?.();
    form.setFieldsValue({ tags: [DEFAULT_ROW] });
    setPartialMatchStates([false]);
    setChanged(false);
    setError(false);
  }, [clearFilters, form]);

  return (
    <div className="ac-column-filter-key-value">
      <div className="ac-column-filter-key-value__content">
        {/* Helper message for partial match */}
        {!hideInfo && hasAnyPartialMatch && (
          <Alert
            className="ac-column-filter-key-value__message"
            type="info"
            description={PARTIAL_MATCH_HELPER}
            showIcon={false}
          />
        )}

        {/* Header row */}
        <Row gutter={8} className="ac-column-filter-key-value__header">
          <Col span={8}>Key</Col>
          <Col span={8}>Value</Col>
          <Col span={6} className="text-center">
            Partial Match
          </Col>
          <Col span={2} />
        </Row>

        {/* Form rows */}
        <Form form={form} onValuesChange={handleFormChange}>
          <Form.List name="tags">
            {(fields, { add, remove }) => (
              <>
                {map(fields, (field, index) => (
                  <Row
                    key={field.key}
                    gutter={8}
                    align="middle"
                    className="m-b-xs"
                  >
                    <Col span={8}>
                      <Form.Item
                        noStyle
                        {...field}
                        name={[field.name, "key"]}
                        rules={[{ required: true, message: "" }]}
                      >
                        <AutoComplete
                          ref={index === 0 ? inputRef : undefined}
                          filterOption={filterOption}
                          onKeyDown={preventEnter}
                          className="w-100"
                          placeholder="Key"
                        >
                          {map(tagKeyOptions, (option, optIndex) => (
                            <AutoComplete.Option key={optIndex} value={option}>
                              {option}
                            </AutoComplete.Option>
                          ))}
                        </AutoComplete>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        noStyle
                        {...field}
                        name={[field.name, "value"]}
                      >
                        <AutoComplete
                          filterOption={filterOption}
                          onKeyDown={preventEnter}
                          className="w-100"
                          placeholder="Value"
                        >
                          {map(getTagValues(index), (option, optIndex) => (
                            <AutoComplete.Option key={optIndex} value={option}>
                              {option}
                            </AutoComplete.Option>
                          ))}
                        </AutoComplete>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Checkbox
                        checked={partialMatchStates[index] ?? false}
                        onChange={(e) =>
                          handlePartialMatchChange(e.target.checked, index)
                        }
                      />
                    </Col>
                    <Col span={2} className="text-center">
                      <ACButton
                        iconOnly
                        type="outline"
                        onClick={() => handleRemove(remove, index)}
                        disabled={fields.length === 1}
                        style={{ width: 32, height: 32 }}
                      >
                        <TrashIcon
                          className={classNames({
                            "text-danger": fields.length > 1,
                          })}
                        />
                      </ACButton>
                    </Col>
                  </Row>
                ))}

                {/* Footer actions */}
                <Row
                  justify="space-between"
                  align="middle"
                  className="ac-column-filter-key-value__footer"
                >
                  <ACButton
                    leftIcon={<PlusIcon />}
                    size="sm"
                    type="secondary"
                    onClick={() => handleAdd(add)}
                    disabled={limit !== undefined && fields.length >= limit}
                  >
                    Add Tag
                  </ACButton>
                  <div>
                    <ACButton
                      className="m-r-xs"
                      size="sm"
                      type="outline"
                      onClick={handleClear}
                    >
                      Clear
                    </ACButton>
                    <ACButton
                      type="primary"
                      size="sm"
                      onClick={handleApply}
                      disabled={!changed}
                    >
                      Apply
                    </ACButton>
                  </div>
                </Row>
              </>
            )}
          </Form.List>
        </Form>

        {/* Error message */}
        {error && (
          <Typography.Text type="danger" className="m-t-xs">
            Key field should not be empty
          </Typography.Text>
        )}
      </div>
    </div>
  );
}

/**
 * Static method for formatting tag labels in filter chips
 */
ACColumnFilterKeyValue.filterLabelFormatter = formatTagLabel;

export default ACColumnFilterKeyValue;
